import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import { Lock, Check } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

const Reset = () => {
  const t = useT();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) { toast.error(t("reset.mismatch")); return; }
    if (pwd.length < 8) { toast.error(t("reset.too_short")); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => nav("/portal", { replace: true }), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("reset.failed"));
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="rn3-container py-20 text-center" data-testid="reset-no-token">
        <h1 className="font-serif-display text-3xl">{t("reset.invalid_link")}</h1>
        <Link to="/portal/forgot" className="rn3-btn-gold mt-6 inline-flex">{t("reset.request_new")}</Link>
      </div>
    );
  }

  if (done) {
    return (
      <div className="rn3-container py-20 text-center" data-testid="reset-success">
        <Check className="w-12 h-12 text-[#C4A252] mx-auto" />
        <h1 className="font-serif-display text-4xl mt-4">{t("reset.success_title")}</h1>
        <div className="border-t border-[#C4A252] w-12 mx-auto my-5" />
        <p className="text-[#404040]">{t("reset.success_body")}</p>
      </div>
    );
  }

  return (
    <div className="rn3-container py-20" data-testid="reset-page">
      <div className="max-w-md mx-auto">
        <div className="rn3-eyebrow text-center flex items-center justify-center gap-2"><Lock className="w-4 h-4" /> {t("reset.eyebrow")}</div>
        <h1 className="font-serif-display text-4xl mt-3 text-center">{t("reset.title")}</h1>
        <div className="border-t border-[#C4A252] w-12 mx-auto my-5" />
        <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
          <div>
            <label className="rn3-eyebrow block mb-2">{t("reset.new_password")}</label>
            <PasswordInput required value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-pwd" autoFocus minLength={8} />
          </div>
          <div>
            <label className="rn3-eyebrow block mb-2">{t("reset.confirm_password")}</label>
            <PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" minLength={8} />
          </div>
          <button disabled={busy} type="submit" className="rn3-btn-primary w-full justify-center" data-testid="reset-submit">
            {busy ? "…" : t("reset.submit")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Reset;
